import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useFetchSearchPsychologist } from '@avanta/shared/hooks/useFetchSearchPsychologist';
import { type Psychologist } from '@avanta/shared/types/psychologists';
import { ChevronLeftIcon, Search } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  type AppointmentsReserveFormData,
  AppointmentsReservePayload,
  type StepProps,
} from 'pages/AppointmentsReserve/types';
import { StepSection } from '../../ui/StepSection';
import { PsychoCard } from './components/PsychoCard';
import { PsychologistStepSkeleton } from './components/PsychologistStepSkeleton';

export const SEARCH_PSYCHOLOGISTS_KEY_PREFIX =
  'AppointmentsReserve.SearchPsychologist.Steps.Psychologist.';

const PsychologistSearchResults = ({
  psychologists,
  isLoading,
  showEmptyState,
  onPsychologistSelect,
}: {
  psychologists: Psychologist[];
  isLoading: boolean;
  showEmptyState: boolean;
  onPsychologistSelect: (psychologist: Psychologist) => void;
}): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  if (isLoading) return <PsychologistStepSkeleton />;

  if (showEmptyState) {
    return (
      <Text variant="text-sm-regular" className="text-denim-500">
        {t(`${SEARCH_PSYCHOLOGISTS_KEY_PREFIX}notFound`)}
      </Text>
    );
  }

  if (!psychologists.length) return null;

  return (
    <>
      {psychologists.map(psychologist => (
        <PsychoCard
          key={psychologist.id}
          psychologist={psychologist}
          onClick={() => onPsychologistSelect(psychologist)}
        />
      ))}
    </>
  );
};

export const PsychologistStep = ({ handleNext }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const { data: psychologists = [], isLoading } = useFetchSearchPsychologist({
    name: debouncedSearch,
    insurance: true,
  });
  const hasSearch = Boolean(search && debouncedSearch);
  const { setValue, reset } = useFormContext<AppointmentsReserveFormData>();

  const handleOnClick = (psychologist: Psychologist): void => {
    setValue(AppointmentsReservePayload.psychologist, psychologist);
    handleNext?.();
  };

  const handleOnBack = (): void => {
    reset(formValues => ({
      ...formValues,
      [AppointmentsReservePayload.specialityBooked]: undefined,
      [AppointmentsReservePayload.psychologistServiceBooked]: undefined,
      [AppointmentsReservePayload.city]: '',
      [AppointmentsReservePayload.backofficeCenterId]: '',
      [AppointmentsReservePayload.dateTime]: '',
      [AppointmentsReservePayload.psychologist]: undefined,
      [AppointmentsReservePayload.flow]: FLOW_MODES.regular,
    }));
  };

  return (
    <div className="h-full relative w-4xl flex flex-col items-center">
      <StepSection
        title={t(`${SEARCH_PSYCHOLOGISTS_KEY_PREFIX}title`)}
        className="flex flex-col min-h-0 gap-5"
        containerClassName="w-xl h-full"
      >
        <Input
          placeholder={t(`${SEARCH_PSYCHOLOGISTS_KEY_PREFIX}search.placeholder`)}
          leftIcon={Search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-137.5"
        />
        <div className="flex flex-col gap-4 overflow-y-auto pb-3 min-h-0 scrollbar-custom w-full max-w-138">
          {hasSearch ? (
            <PsychologistSearchResults
              psychologists={psychologists}
              isLoading={isLoading}
              showEmptyState={!isLoading && !psychologists.length}
              onPsychologistSelect={handleOnClick}
            />
          ) : null}
        </div>
      </StepSection>
      <Button
        variant="terciary"
        onClick={handleOnBack}
        icon="back"
        size="lg"
        className="w-fit absolute bottom-5 left-2"
      >
        <ChevronLeftIcon className="w-4 h-4" />
        {t('Common.back', { ns: LOCIZE_NS.COMMON })}
      </Button>
    </div>
  );
};
